import {compact} from '@benchling/js-utils';
import {v4 as uuidv4} from 'uuid';

import {assert} from 'Asserts';
import {ProcessFlowchartSchemaMapperSpec} from 'processes/dao/ProcessFlowchartConfigDAO';
import {getClientIdForProcessFlowchartEdge} from 'processes/graph-builder-processes/flowchart-viz/util/ConvertProcessFlowchartForReactFlowUtil';
import {
  ProcessEdgeForValidation,
  ProcessFlowchartForValidation,
  ProcessNodeForValidation,
} from 'processes/graph-builder-processes/validation/ProcessFlowchartForValidationTypes';
import {ProcessFlowchartDesignSpace} from 'processes/graph-builder-processes/validation/ProcessGraphBuilderConstants';
import {
  FlowchartStaticEdgeData,
  ProcessFlowchartEdgeType,
  ProcessFlowchartNodeType,
} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorConstants';
import {assertNever} from 'util/Types';

import {ProcessTaskSchemaWithoutFlowchartConfigGraphQL} from '../../../../../graphql-fragments/processes/schemas/__generated/ProcessTaskSchemaWithoutFlowchartConfigGraphQL';
import buildSchemaMapperConfigsFromStaticData from './buildSchemaMapperConfigFromStaticData';
import {isStaticTaskSchemaNodeData, StaticFlowchartNodeData} from './processFlowchartConfigEditorNodeTypes';
import {isLookupConfiguredForEdge} from './processFlowchartConfigEditorUtils';
import {FlowchartConfigMetadata} from './redux/reduceFlowchartConfig';
import {getDefaultTransitionSource, getTransitionSourceByNodeIdPairKey} from './TransitionSourceUtils';
interface ValidationFlowchartData {
  flowchart: ProcessFlowchartForValidation;
  designSpace: ProcessFlowchartDesignSpace;
}

interface BuildValidationFlowchartFromStaticDataRunnerOptions
  extends Omit<FlowchartConfigMetadata, 'interactionState'> {}

interface BuildValidationFlowchartFromStaticDataRunnerArgs {
  id: string;
  staticNodesData: Array<StaticFlowchartNodeData>;
  staticEdgesData: Array<FlowchartStaticEdgeData>;
  options: BuildValidationFlowchartFromStaticDataRunnerOptions;
}

class BuildValidationFlowchartFromStaticDataRunner {
  private readonly id: string;
  private readonly staticNodesData: Array<StaticFlowchartNodeData>;
  private readonly staticEdgesData: Array<FlowchartStaticEdgeData>;
  private readonly options: BuildValidationFlowchartFromStaticDataRunnerOptions;

  private rootTaskSchema: ProcessTaskSchemaWithoutFlowchartConfigGraphQL;
  private inboundSchemaMapperConfigsByNodeId: Record<string, Array<ProcessFlowchartSchemaMapperSpec>>;

  private nodes: Array<ProcessNodeForValidation>;
  private edges: Array<ProcessEdgeForValidation>;
  private designSpace: ProcessFlowchartDesignSpace | null;

  constructor(args: BuildValidationFlowchartFromStaticDataRunnerArgs) {
    this.id = args.id;
    this.staticNodesData = args.staticNodesData;
    this.staticEdgesData = args.staticEdgesData;
    this.options = args.options;
    assert(this.options.rootTaskSchema != null, 'Expected root task schema to be defined');
    this.rootTaskSchema = this.options.rootTaskSchema;
    this.inboundSchemaMapperConfigsByNodeId = {};
    this.nodes = [];
    this.edges = [];
    this.designSpace = null;
  }

  run(): ValidationFlowchartData {
    this.buildSchemaMapperConfigs();
    this.convertStaticDataToNodes();
    this.convertStaticDataToEdges();
    this.buildDesignSpace();
    assert(this.designSpace != null, 'Design space is required');
    return {
      flowchart: {
        id: this.id,
        nodes: this.nodes,
        edges: this.edges,
      },
      designSpace: this.designSpace,
    };
  }

  private buildSchemaMapperConfigs(): void {
    const {inboundSchemaMapperConfigsByNodeId} = buildSchemaMapperConfigsFromStaticData({
      nodes: this.staticNodesData,
      edges: this.staticEdgesData,
      customMappingDataByEdgeId: this.options.customMappingDataByEdgeId,
      transitionSourceByNodeIdPair: this.options.transitionSourceByNodeIdPair,
    });
    this.inboundSchemaMapperConfigsByNodeId = inboundSchemaMapperConfigsByNodeId;
  }

  private convertStaticDataToNodes(): void {
    this.nodes = this.staticNodesData
      .filter((staticNodeData) => staticNodeData.nodeType !== ProcessFlowchartNodeType.PLACEHOLDER_NODE)
      .map((staticNodeData) => ({
        ...staticNodeData,
        name: staticNodeData.nodeName,
        nodeType: staticNodeData.nodeDetailsType,
        executionType: isStaticTaskSchemaNodeData(staticNodeData)
          ? staticNodeData.taskDefinitionVersionSpec.newProcessTaskDefinitionVersion.taskSchema.executionType
          : undefined,
      })) as Array<ProcessNodeForValidation>;
  }

  private convertStaticDataToEdges(): void {
    this.edges = this.staticEdgesData.map((staticEdgeData): ProcessEdgeForValidation => {
      const nodeIdPairKey = getTransitionSourceByNodeIdPairKey(staticEdgeData.source, staticEdgeData.target);
      if (staticEdgeData.type === ProcessFlowchartEdgeType.DATA_FLOW) {
        return {
          ...staticEdgeData,
          isLookupConfigured: isLookupConfiguredForEdge(
            staticEdgeData.id,
            this.options.customMappingDataByEdgeId
          ),
          transitionSource:
            this.options.transitionSourceByNodeIdPair[nodeIdPairKey] ??
            getDefaultTransitionSource(staticEdgeData.source, this.staticNodesData),
        };
      } else if (staticEdgeData.type === ProcessFlowchartEdgeType.CONTROL_FLOW) {
        return {
          ...staticEdgeData,
          type: ProcessFlowchartEdgeType.CONTROL_FLOW,
        };
      } else {
        throw new Error(`Unexpected edge type: ${assertNever(staticEdgeData.type)}`);
      }
    });
  }

  private buildDesignSpace(): void {
    // Constructs available nodes and edges based on currently placed nodes and data flow edges
    // Get a map of task schema IDs to nodes that implement that task schema
    const schemaIdToNodeIds = new Map<string, Array<string>>();
    for (const node of this.staticNodesData) {
      if (node.nodeType === ProcessFlowchartNodeType.TASK_SCHEMA_NODE) {
        schemaIdToNodeIds.set(node.taskSchemaId, [
          ...(schemaIdToNodeIds.get(node.taskSchemaId) ?? []),
          node.id,
        ]);
      }
    }
    const nodeIdToSchemaId = new Map<string, string>();
    for (const [schemaId, nodeIds] of schemaIdToNodeIds.entries()) {
      for (const nodeId of nodeIds) {
        nodeIdToSchemaId.set(nodeId, schemaId);
      }
    }

    // Get a map of data flow edge source to targets so we can construct available control flow edges
    const dataFlowEdgeSourceToTargetsMap = new Map<string, Array<string>>();
    for (const edge of this.staticEdgesData) {
      if (edge.type === ProcessFlowchartEdgeType.DATA_FLOW) {
        dataFlowEdgeSourceToTargetsMap.set(edge.source, [
          ...(dataFlowEdgeSourceToTargetsMap.get(edge.source) ?? []),
          edge.target,
        ]);
      }
    }

    this.designSpace = {
      // Available nodes are based on task schemas that have been placed in the flowchart.
      // Their inbound schema mappers are based on data flow edges that have been drawn.
      availableNodes: Array.from(schemaIdToNodeIds.entries()).map(([schemaId, nodeIds]) => ({
        clientId: schemaId,
        schemaId,
        name: schemaId,
        inboundMapperConfigs: nodeIds
          .map(
            (nodeId) =>
              this.inboundSchemaMapperConfigsByNodeId[nodeId]?.map((config) => ({
                clientId: config.schemaMapperConfigClientId ?? uuidv4(),
                fromTaskSchemaIds: config.schemaMapperDependencies.map(
                  (source) => source.fromNodeConfigClientId
                ),
                toTaskSchemaId: config.toNodeConfigClientId,
              })) ?? []
          )
          .flat(),
      })),

      // Available edges are based on data flow edges that have been drawn.
      availableEdges: Array.from(dataFlowEdgeSourceToTargetsMap.entries()).flatMap(([source, targets]) =>
        targets.map((target) => ({
          clientId: getClientIdForProcessFlowchartEdge(source, target),
          fromNodeId: source,
          toNodeId: target,
        }))
      ),

      flowchartSchema: this.rootTaskSchema,

      flowchartConfig: {
        availableTaskSchemas: Array.from(schemaIdToNodeIds.keys()).map((schemaId) => ({
          id: schemaId,
          // Schema name is not used in validation, so we don't need to actually set it to something - could be cleaned up
          name: schemaId,
        })),
        schemaMapperConfigs: compact(
          this.options.schemaMapperConfigs.map((config) => {
            if (config.toTaskSchema != null && config.mapperConfig != null) {
              return {
                clientId: config.clientId,
                fromTaskSchemas: config.fromTaskSchemas.map((schema) => ({
                  id: schema.id,
                  name: schema.name,
                })),
                toTaskSchema: {
                  id: config.toTaskSchema.id,
                  name: config.toTaskSchema.name,
                },
                mapperConfig: config.mapperConfig,
              };
            } else {
              return null;
            }
          })
        ),
      },
    };
  }
}

export default function buildValidationFlowchartFromStaticData(
  args: BuildValidationFlowchartFromStaticDataRunnerArgs
): ValidationFlowchartData {
  const runner = new BuildValidationFlowchartFromStaticDataRunner(args);
  return runner.run();
}
